import { trackEvent } from '@pm/analytics';
import {
  DocumentBasedLabResult,
  LabKit,
  LabRequisition,
  LabWork,
} from '@pm/graphql';
import {
  AccordionListItem,
  Button,
  ButtonLink,
  Icon,
  IconButton,
  IconTile,
} from '@pm/ui';
import { useToggle } from '@pm/util';
import { useTranslation } from 'react-i18next';
import { useEnvironment } from '../../hooks/env';
import { usePatientProfile } from '../../hooks/profile';
import { useShipmentStatus } from '../../hooks/use-shipment-status/useShipmentStatus';
import { labsRoutes } from '../../modules/labs/labs-routes';
import { formatDateToLongFormYear } from '../../utilities/dateUtilities';
import { COMPLETE_SHIPMENT_STATUSES } from '../../utilities/shipment';
import { LabActions } from './LabActions';
import { LabDetailItem } from './LabDetailItem';
import { LabKitShipmentModal } from './LabKitShipmentModal';
import { isLabDetail, isLabKit, isLabReq, isLabResult } from './type-guards';
import { useMarkRequisitionViewed } from './useMarkRequisitionViewed';
import { toLabRequisitionUrl } from './utils';

type LabListItemProps = {
  labWork: LabWork;
  supportLink?: string;
  labFeedbackUrl?: string | null;
  isNew?: boolean;
};

type LabKitListItemProps = {
  labKit: LabKit;
  supportLink?: string;
};

export const LabListItem = ({
  labWork,
  supportLink,
  labFeedbackUrl,
  isNew,
}: LabListItemProps) => {
  if (isLabKit(labWork)) {
    return <LabKitListItem labKit={labWork} supportLink={supportLink} />;
  }

  if (isLabReq(labWork)) {
    return (
      <LabRequisitionListItem
        labReq={labWork}
        labFeedbackUrl={labFeedbackUrl}
        isNew={isNew}
      />
    );
  }

  if (isLabResult(labWork)) {
    return <LabResultListItem labResult={labWork} />;
  }

  if (isLabDetail(labWork)) {
    return <LabDetailItem labDetail={labWork} />;
  }

  return null;
};

const LabKitListItem = ({ labKit, supportLink = '' }: LabKitListItemProps) => {
  const { i18n, t } = useTranslation(['labs', 'shipment']);
  const { status, description, trackingNumber, shouldShowSupportLink } =
    useShipmentStatus({
      inboundShipment: labKit?.inboundShipment,
      outboundShipment: labKit?.outboundShipment,
    });
  const [showModal, handlers] = useToggle();
  const isCompleted = COMPLETE_SHIPMENT_STATUSES.includes(
    labKit.inboundShipment
      ?.status as (typeof COMPLETE_SHIPMENT_STATUSES)[number],
  );

  const handleOpenModal = () => handlers.on();
  const handleCloseModal = () => handlers.off();

  const formattedDate = formatDateToLongFormYear(
    new Date(labKit.createdAt),
    i18n.language,
  );

  const baseProps = {
    value: labKit.id,
    heading: t('labs:LabKit'),
    'aria-label': t('labs:LabKitAriaLabel', { date: formattedDate }),
    grouping: 'joined' as const,
    leadingElement: (
      <img
        src="/images/lab-kit-bg.webp"
        alt={t('labs:LabKit')}
        className="w-[40px] h-[40px] rounded-xs"
      />
    ),
    background: 'white' as const,
  };

  return (
    <>
      <AccordionListItem
        {...baseProps}
        body={formattedDate}
        onClick={handleOpenModal}
        trailingElement={
          isCompleted ? (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleOpenModal();
              }}
              aria-label={t('shipment:LabKit.KitModalDetails')}
              intent="tertiary"
              size="xSmall"
            >
              <Icon name="chevron_right" size="large" />
            </IconButton>
          ) : (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                handleOpenModal();
              }}
              aria-label={t('shipment:LabKit.KitModalDetails')}
              intent="tertiary"
              size="xSmall"
              trailingIcon={<Icon name="chevron_right" size="large" />}
            >
              {t('shipment:LabKit.Track')}
            </Button>
          )
        }
      />

      <LabKitShipmentModal
        status={status}
        description={description}
        trackingNumber={trackingNumber}
        isOpen={showModal}
        onClose={handleCloseModal}
        supportLink={supportLink}
        shouldShowSupportLink={shouldShowSupportLink}
      />
    </>
  );
};

const LabRequisitionListItem = ({
  labReq,
  labFeedbackUrl,
  isNew = false,
}: {
  labReq: LabRequisition;
  labFeedbackUrl?: string | null;
  isNew?: boolean;
}) => {
  const { i18n, t } = useTranslation('labs');
  const { isUsaPatient } = usePatientProfile();
  const { apiHost } = useEnvironment();

  const ownFeedbackUrl =
    !isUsaPatient && labReq.feedbackStep?.id && !labReq.feedbackStep.completedAt
      ? `/lab-feedback/${labReq.feedbackStep.id}`
      : null;
  const effectiveFeedbackUrl = labFeedbackUrl ?? ownFeedbackUrl;
  const showLabFeedbackButton = (isNew && !!labFeedbackUrl) || !!ownFeedbackUrl;
  const formattedDate = formatDateToLongFormYear(
    new Date(labReq.createdAt),
    i18n.language,
  );

  const rawDownloadUrl =
    labReq.attachments?.[0]?.downloadUrl ?? labReq.formLink ?? null;
  const downloadUrl = rawDownloadUrl
    ? toLabRequisitionUrl(rawDownloadUrl, apiHost)
    : null;

  const { markAsViewed } = useMarkRequisitionViewed(labReq);

  // Opening the requisition (any way) must record the view, otherwise the
  // "viewed" status never updates. LabActions handles this for the feedback-button
  // case; the row click and download icon below must do the same.
  const openRequisition = () => {
    if (!downloadUrl) {
      return;
    }
    markAsViewed();
    window.open(downloadUrl, '_blank', 'noopener,noreferrer');
  };

  const handleRowClick = downloadUrl ? openRequisition : undefined;

  // when there's a downloadUrl, the row itself is clickable (handleRowClick).
  // any interactive element in the trailing slot must stop propagation to
  // prevent the row handler from also firing and opening a second tab.
  const trailingElement = downloadUrl ? (
    !showLabFeedbackButton ? (
      // without a feedback button: show a plain download icon.
      // stopPropagation lives on the button itself, so no wrapper needed
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          openRequisition();
        }}
        aria-label={t('Download')}
        intent="tertiary"
        size="xSmall"
      >
        <Icon name="download" size="medium" />
      </IconButton>
    ) : (
      // feedback button visible: delegate to LabActions
      // role="none" marks the span as presentational so the onClick used for
      // stopPropagation doesn't violate any a11y interaction rules
      <span role="none" onClick={(e) => e.stopPropagation()}>
        <LabActions labWork={labReq} isNew={isNew} />
      </span>
    )
  ) : (
    // no downloadUrl means the row isn't clickable, so no propagation concern ✅
    <LabActions labWork={labReq} isNew={isNew} />
  );

  return (
    <AccordionListItem
      value={labReq.id}
      grouping="joined"
      heading={t('Requisition')}
      body={formattedDate}
      aria-label={t('RequisitionAriaLabel', { date: formattedDate })}
      onClick={handleRowClick}
      trailingElement={trailingElement}
      leadingElement={
        <img
          src="/images/lab-req-bg.webp"
          alt={t('Requisition')}
          className="w-[40px] h-[40px] rounded-xs"
        />
      }
      background="white"
    >
      {showLabFeedbackButton && (
        <LabFeedbackButton labFeedbackUrl={effectiveFeedbackUrl}>
          {t('FeedbackButton')}
        </LabFeedbackButton>
      )}
    </AccordionListItem>
  );
};

const LabResultListItem = ({
  labResult,
}: {
  labResult: DocumentBasedLabResult;
}) => {
  const { i18n, t } = useTranslation('labs');

  const formattedDate = formatDateToLongFormYear(
    new Date(labResult.documentUploadedAt ?? labResult.createdAt),
    i18n.language,
  );

  const isFromLabKit = labResult.labKit != null;

  const hasOrder = isFromLabKit || labResult.labRequisition != null;

  const handleViewResults = () => {
    // All access of lab results should be through LabDocumentRedirectPage and
    // not through the document URL directly on the result. This is in order
    // to ensure backend document access logging is properly captured
    // this tracking occurs on the backend in the getDocumentBasedLabResult query resolver
    if (labResult.id) {
      const url = labsRoutes.document.build({ id: labResult.id });
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleRowClick =
    hasOrder || labResult.documentUrl ? handleViewResults : undefined;

  const trailingElement = isFromLabKit ? (
    <Icon name="chevron_right" size="large" />
  ) : hasOrder ? (
    <Icon name="open_in_new" size="medium" />
  ) : labResult.documentUrl ? (
    <Icon name="download" size="medium" />
  ) : null;

  return (
    <AccordionListItem
      value={labResult.id}
      heading={isFromLabKit ? t('LabKit') : t('LabResults')}
      body={formattedDate}
      grouping="joined"
      aria-label={
        isFromLabKit
          ? t('LabKitAriaLabel', { date: formattedDate })
          : hasOrder
            ? t('LabResultsViewAriaLabel', { date: formattedDate })
            : labResult.documentUrl
              ? t('LabResultsDownloadAriaLabel', { date: formattedDate })
              : t('LabResultsAriaLabel', { date: formattedDate })
      }
      leadingElement={
        isFromLabKit ? (
          <img
            src="/images/lab-kit-bg.webp"
            alt={t('LabKit')}
            className="w-[40px] h-[40px] rounded-xs"
          />
        ) : (
          <IconTile icon="task" size="large" intent="positive" />
        )
      }
      trailingElement={trailingElement}
      onClick={handleRowClick}
      background="white"
      collapsible={false}
    />
  );
};

const LabFeedbackButton = ({
  labFeedbackUrl,
  children,
}: {
  labFeedbackUrl?: string | null;
  children: string;
}) => {
  return (
    labFeedbackUrl != null && (
      <ButtonLink
        to={labFeedbackUrl}
        width="full"
        onClick={() => {
          trackEvent('Button Clicked', {
            buttonText: children,
            buttonName: 'lab_feedback_button',
            currentPageName: 'Lab Work',
            currentPath: window.location.pathname,
            targetPath: labFeedbackUrl,
          });
        }}
      >
        {children}
      </ButtonLink>
    )
  );
};
